import { Link } from 'react-router-dom';
import { 
  ArrowRight, BookOpen, Users, Globe, Lightbulb, 
  FileText, ExternalLink, Award, Target, Microscope,
  Network, Shield
} from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

const researchAreas = [
  {
    title: 'Public Health Research',
    description: 'Investigating disease patterns, health interventions, and healthcare delivery systems.',
    icon: Microscope,
  },
  {
    title: 'Environmental Studies',
    description: 'Research on environmental health, climate impact, and sustainable practices.',
    icon: Globe,
  },
  {
    title: 'Agricultural Innovation',
    description: 'Developing sustainable farming techniques and food security solutions.',
    icon: Lightbulb,
  },
  {
    title: 'Educational Research',
    description: 'Improving teaching methodologies and learning outcomes in Africa.',
    icon: BookOpen,
  },
];

const researchPlatforms = [
  {
    name: 'CEPRES International Journal of Science and Innovative Studies',
    type: 'Open-access, peer-reviewed international journal',
    description: 'Our flagship academic publication reinforcing CIU\'s position as a research hub in Africa, particularly in West Africa and Liberia.',
    link: null,
  },
];

export default function Research() {
  return (
    <div data-testid="research-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.research} url="/research" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Research & <span className="text-[#C5A028]">Innovation</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            Research is central to the CIU academic journey. We instill a strong research 
            culture in our students to address local and global challenges.
          </p>
        </div>
      </section>

      {/* Research Hub Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[#C5A028] text-[#1A1A1A] text-sm font-semibold mb-4">
                RESEARCH HUB IN AFRICA
              </span>
              <h2 className="text-4xl font-bold text-[#0D4A2E] mb-6">
                Driving Innovation Through <span className="text-[#C5A028]">Research</span>
              </h2>
              <p className="text-[#666666] mb-6 leading-relaxed">
                At CEPRES International University, research is not just an academic exercise—it's 
                a tool for transformation. We equip our students with research skills that enable 
                them to tackle real-world challenges in their communities and beyond.
              </p>
              <p className="text-[#666666] leading-relaxed">
                One of our flagship outputs is the CEPRES International Journal of Science and 
                Innovative Studies, reinforcing CIU's position as a research hub in Africa, 
                particularly in West Africa and Liberia.
              </p>
            </div>
            <div className="bg-[#F5F5F5] p-8">
              <Microscope className="w-16 h-16 text-[#C5A028] mb-6" />
              <h3 className="text-2xl font-bold text-[#0D4A2E] mb-4">Research Focus</h3>
              <p className="text-[#666666] mb-6">
                Our research addresses critical challenges at the intersection of:
              </p>
              <ul className="space-y-3">
                {['Applied Health Sciences', 'Agricultural Innovation', 'Environmental Sustainability', 'Data and Technology', 'Educational Development'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-[#C5A028]" />
                    <span className="text-[#0D4A2E]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section data-testid="research-areas" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Research Areas</h2>
          <p className="text-[#666666] mb-12 max-w-2xl">
            Our faculty and students conduct impactful research across multiple disciplines.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {researchAreas.map((area, index) => (
              <div 
                key={index}
                className="bg-white p-8 border-t-4 border-[#C5A028] hover:shadow-lg transition-shadow duration-300"
              >
                <area.icon className="w-12 h-12 text-[#0D4A2E] mb-4" />
                <h3 className="text-lg font-bold text-[#0D4A2E] mb-3">{area.title}</h3>
                <p className="text-[#666666] text-sm">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Directorate of Research */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">
                Directorate of Research & <span className="text-[#C5A028]">Institutional Development</span>
              </h2>
              <p className="text-[#666666] mb-8 leading-relaxed">
                Oversees research strategy, training, and institutional development under the 
                Vice President for Research. The Directorate ensures that CIU maintains the 
                highest standards of research excellence and ethical compliance.
              </p>

              <div className="space-y-6">
                <div className="bg-[#F5F5F5] p-6 border-l-4 border-[#0D4A2E]">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-8 h-8 text-[#0D4A2E]" />
                    <h3 className="text-lg font-bold text-[#0D4A2E]">Research & Ethics Review Board (REB)</h3>
                  </div>
                  <p className="text-[#666666]">
                    Ensures ethical compliance and approval of all human subject research conducted 
                    under CIU. All research involving human participants must receive REB approval 
                    before commencement.
                  </p>
                </div>

                <div className="bg-[#F5F5F5] p-6 border-l-4 border-[#C5A028]">
                  <div className="flex items-center gap-3 mb-3">
                    <Award className="w-8 h-8 text-[#C5A028]" />
                    <h3 className="text-lg font-bold text-[#0D4A2E]">Research Impact</h3>
                  </div>
                  <p className="text-[#666666]">
                    Our research outputs contribute to policy development, community health 
                    improvements, and sustainable development across Liberia and West Africa.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="bg-[#0D4A2E] text-white p-8 pattern-overlay">
              <Network className="w-12 h-12 text-[#C5A028] mb-6" />
              <h3 className="text-xl font-bold mb-4">Research Networks & Platforms</h3>
              {researchPlatforms.map((platform, index) => (
                <div key={index} className="mb-6">
                  <h4 className="font-semibold text-[#C5A028] mb-2">{platform.name}</h4>
                  <p className="text-white/60 text-sm mb-2">{platform.type}</p>
                  <p className="text-white/80 text-sm">{platform.description}</p>
                </div>
              ))}
              <p className="text-white/60 text-sm mt-6">
                CIU maintains partnerships with local and international universities and 
                research organizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Student Research */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="w-16 h-16 text-[#C5A028] mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Student Research Opportunities</h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              Every CIU student engages in research as part of their academic journey. 
              Our programs emphasize hands-on research experience, culminating in capstone 
              projects and thesis work that address real-world challenges.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              {[
                { title: 'Capstone Projects', desc: 'Applied research addressing community needs' },
                { title: 'Faculty Mentorship', desc: 'Guidance from experienced researchers' },
                { title: 'Publication Opportunities', desc: 'Chance to publish in our journal' },
              ].map((item, index) => (
                <div key={index} className="bg-white/5 border border-white/10 p-6">
                  <h4 className="font-bold text-[#C5A028] mb-2">{item.title}</h4>
                  <p className="text-white/60 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Join Our Research Community</h2>
          <p className="text-[#666666] mb-8">
            Become part of a university that values research as a pathway to impact.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/programs"
              className="inline-flex items-center gap-2 bg-[#0D4A2E] text-white px-8 py-4 font-bold hover:bg-[#082D1C] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              EXPLORE PROGRAMS
            </Link>
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              APPLY NOW
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
